import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Dropdown from "components/dropdown";
import NotificationBell from "components/NotificationBell";
import { Link, useNavigate } from "react-router-dom";
import { RiMoonFill, RiSunFill } from "react-icons/ri";
import { getToken, removeToken } from "utils/auth";
import { API_BASE_URL } from "utils/api";
import { resolvePortalPath } from "utils/portalPath";
import { useUser } from "contexts/UserContext";

const getInitials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "U";

const Navbar = (props) => {
  const { brandText } = props;
  const navigate = useNavigate();
  const { user, setUser, setUserFromApi } = useUser();

  useEffect(() => {
    const token = getToken();
    if (!token || user) return; // Skip if already loaded

    axios
      .get(`${API_BASE_URL}/users/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setUserFromApi(res.data?.data || res.data))
      .catch(() => { });
  }, [user, setUserFromApi]);

  const handleLogout = () => {
    removeToken();
    setUser(null);
    navigate("/auth/sign-in", { replace: true });
  };

  const breadcrumbMap = {
    "lead folder": "folders/leads",
    "client folder": "folders/clients",
    "folders": "folders",
    "lead management": "lead-management",
    "client management": "client-management",
    "careof management": "careof-management",
    "staff management": "staff-management",
  };

  const normalizeBreadcrumbSegment = (segment) => {
    const normalized = segment.trim();
    const lookup = breadcrumbMap[normalized.toLowerCase()];
    if (lookup) return lookup;
    return normalized.replace(/\s+/g, "-").toLowerCase();
  };

  // derive an array from brandText. if it contains a slash we split into two parts,
  // otherwise keep a single entry. this makes it easier to render breadcrumbs/etc.
  const brandItems = React.useMemo(() => {
    if (!brandText || typeof brandText !== "string") {
      return [""];
    }
    return brandText.includes("/") ? brandText.split("/") : [brandText];
  }, [brandText]);
  const [darkmode, setDarkmode] = React.useState(false);
  const breadcrumbRef = useRef(null);
  const [mobileGap, setMobileGap] = useState(0);

  React.useEffect(() => {
    const updateGap = () => {
      const h = breadcrumbRef.current?.offsetHeight || 0;
      // Tailwind `md` breakpoint is 768px — only apply gap on smaller screens
      if (typeof window !== "undefined" && window.innerWidth < 768) {
        // use a smaller extra spacing (4px) and cap the gap to avoid large jumps
        const gap = Math.min(h + 4, 20);
        setMobileGap(gap);
      } else {
        setMobileGap(0);
      }
    };

    updateGap();
    window.addEventListener("resize", updateGap);
    return () => window.removeEventListener("resize", updateGap);
  }, [brandItems]);

  return (
    <nav className="sticky top-4 z-40 flex flex-row flex-wrap items-center justify-between rounded-xl bg-white/10 p-2 backdrop-blur-xl dark:bg-[#0b14374d]">
      <div>
        <div ref={breadcrumbRef} className="w-full pt-1">
          <Link
            className="text-sm font-normal text-navy-700 hover:underline dark:text-white dark:hover:text-white"
            to={resolvePortalPath("/dashboard", user?.role)}
          >
            {(user?.companyShortName || user?.company_short_name)?.trim() || "Mellbizz"}
          </Link>
          <span className="mx-1 text-sm text-navy-700 dark:text-white">
            {" "}
            /{" "}
          </span>
          {brandItems.map((item, idx) => {
            // build a url segment containing every breadcrumb up to this one
            const path = brandItems
              .slice(0, idx + 1)
              .map((p) => normalizeBreadcrumbSegment(p))
              .join("/");

            const isLast = idx === brandItems.length - 1;

            return (
              <React.Fragment key={idx}>
                {isLast ? (
                  <span
                    className="text-sm font-semibold capitalize text-brand-500 dark:text-brand-300"
                    aria-current="page"
                  >
                    {item.trim()}
                  </span>
                ) : (
                  <Link
                    className="text-sm font-normal capitalize text-navy-700 hover:underline dark:text-white dark:hover:text-white"
                    to={resolvePortalPath(path, user?.role)}
                  >
                    {item.trim()}
                  </Link>
                )}
                {!isLast && (
                  <span className="mx-1 text-sm text-navy-700 hover:text-navy-700 dark:text-white">
                    /
                  </span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

        <div
          className="relative flex h-[61px] w-full max-w-[420px] flex-grow items-center justify-between gap-2 rounded-full bg-white px-2 py-2 shadow-xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none md:flex-grow-0 md:gap-1 xl:max-w-[460px] xl:gap-2"
          style={
            mobileGap
              ? { marginTop: `${mobileGap}px`, transition: "margin-top 180ms ease" }
              : { transition: "margin-top 180ms ease" }
          }
        >
        <div className="flex items-center gap-2">
          <a
            href="https://primebizz.mellbizz.in"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-semibold text-brand-500 transition hover:border-brand-300 hover:bg-brand-50 dark:border-white/10 dark:bg-navy-800 dark:text-brand-300 dark:hover:bg-navy-700"
          >
            Primebizz
          </a>
         
        </div>
        {/* start Notification */}
        <NotificationBell />
        {/* start Horizon PRO */}
        {/* <Dropdown
          button={
            <p className="cursor-pointer">
              <IoMdInformationCircleOutline className="h-4 w-4 text-gray-600 dark:text-white" />
            </p>
          }
          children={
            <div className="flex w-[350px] flex-col gap-2 rounded-[20px] bg-white p-4 shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none">
              <div
                style={{
                  backgroundImage: `url(${navbarimage})`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                }}
                className="mb-2 aspect-video w-full rounded-lg"
              />
              <a
                target="blank"
                href="https://horizon-ui.com/pro?ref=live-free-tailwind-react"
                className="px-full linear flex cursor-pointer items-center justify-center rounded-xl bg-brand-500 py-[11px] font-bold text-white transition duration-200 hover:bg-brand-600 hover:text-white active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:bg-brand-200"
              >
                Buy Horizon UI PRO
              </a>
              <a
                target="blank"
                href="https://horizon-ui.com/docs-tailwind/docs/react/installation?ref=live-free-tailwind-react"
                className="px-full linear flex cursor-pointer items-center justify-center rounded-xl border py-[11px] font-bold text-navy-700 transition duration-200 hover:bg-gray-200 hover:text-navy-700 dark:!border-white/10 dark:text-white dark:hover:bg-white/20 dark:hover:text-white dark:active:bg-white/10"
              >
                See Documentation
              </a>
              <a
                target="blank"
                href="https://horizon-ui.com/?ref=live-free-tailwind-react"
                className="hover:bg-black px-full linear flex cursor-pointer items-center justify-center rounded-xl py-[11px] font-bold text-navy-700 transition duration-200 hover:text-navy-700 dark:text-white dark:hover:text-white"
              >
                Try Horizon Free
              </a>
            </div>
          }
          classNames={"py-2 top-6 -left-[250px] md:-left-[330px] w-max"}
          animation="origin-[75%_0%] md:origin-top-right transition-all duration-300 ease-in-out"
        /> */}
        <div
          className="cursor-pointer text-gray-600"
          onClick={() => {
            if (darkmode) {
              document.body.classList.remove("dark");
              setDarkmode(false);
            } else {
              document.body.classList.add("dark");
              setDarkmode(true);
            }
          }}
        >
          {darkmode ? (
            <RiSunFill className="h-4 w-4 text-gray-600 dark:text-white" />
          ) : (
            <RiMoonFill className="h-4 w-4 text-gray-600 dark:text-white" />
          )}
        </div>
        {/* Profile & Dropdown */}
        <Dropdown
          button={
            <div className="flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-brand-500/30 bg-brand-500/20 text-sm font-bold text-brand-600 dark:text-brand-400">
              {user?.profileImage ? (
                <img className="h-full w-full rounded-full object-cover" src={user.profileImage} alt={user.name || "avatar"} />
              ) : (
                <span>{getInitials(user?.name)}</span>
              )}
            </div>
          }
          children={
            <div className="flex w-56 flex-col justify-start rounded-[20px] bg-white bg-cover bg-no-repeat shadow-xl shadow-shadow-500 dark:!bg-navy-700 dark:text-white dark:shadow-none">
              <div className="p-4">
                <div className="flex items-center gap-2">
                  {user?.profileImage ? (
                    <img className="h-8 w-8 flex-shrink-0 rounded-full object-cover" src={user.profileImage} alt={user.name} />
                  ) : (
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-500/20 text-xs font-bold text-brand-600 dark:text-brand-400">
                      {getInitials(user?.name)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-navy-700 dark:text-white">
                      👋 Hey, {user?.name?.split(" ")[0] || "there"}
                    </p>
                    {user?.role && (
                      <p className="truncate text-xs capitalize text-gray-500 dark:text-gray-400">
                        {user.role.replace(/_/g, " ")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="h-px w-full bg-gray-200 dark:bg-white/20 " />

              <div className="flex flex-col gap-2 p-4">
                <Link
                  to={resolvePortalPath("/profile", user?.role)}
                  className="w-full rounded-xl px-3 py-2 text-sm text-gray-800 transition hover:bg-gray-100 dark:text-white dark:hover:bg-white/10"
                >
                  Profile Settings
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full rounded-xl px-3 py-2 text-left text-sm font-medium text-red-500 transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-white/10"
                >
                  Log Out
                </button>
              </div>
            </div>
          }
          classNames={"py-2 top-8 -left-[180px] w-max"}
        />
      </div>
    </nav>
  );
};

export default Navbar;
